<template>
  <div class="earworm-builder-container">
    <Header :title="earworm.name" :user="currentUser" />
    
    <!-- Camera overlay -->
    <div v-if="cameraOpen" class="camera-overlay">
      <div class="camera-modal" @click.stop>
        <div class="camera-preview-container">
          <video ref="videoEl" autoplay playsinline class="camera-video" />
          <div class="document-frame">
            <div class="document-corners">
              <div class="corner top-left"></div>
              <div class="corner top-right"></div>
              <div class="corner bottom-left"></div>
              <div class="corner bottom-right"></div>
            </div>
            <div class="document-guide-text">Position document within frame</div>
          </div>
        </div>
        <div class="camera-actions">
          <button type="button" @click="capturePhoto" class="action-btn capture-btn">📸 Capture Document</button>
          <button type="button" @click="closeCamera" class="action-btn cancel-btn">Close</button>
        </div>
      </div>
    </div>
    
    <main class="page-main">
      <form @submit.prevent="saveEarworm" class="earworm-form">
        <!-- Earworm Name Section -->
        <div class="earworm-name-section">
          <div class="form-group name-form-group">
            <input 
              id="earworm-name"
              v-model="earworm.name" 
              type="text" 
              class="form-input"
              placeholder="Enter a name for this earworm..."
              maxlength="100"
            />
          </div>
        </div>

        <div class="worksheet-toggle-row">
          <div class="worksheet-label">Worksheet</div>
        </div>

        <div 
          class="earworm-upload-section"
          v-show="showWorksheetSection"
          :aria-hidden="showWorksheetSection ? 'false' : 'true'"
        >
          <!-- Left column: Image preview -->
          <div class="image-preview-column">
            <div class="image-preview-area">
              <div v-if="worksheetPreviewMode === 'prompt'" class="image-preview-card prompt-preview-card">
                <textarea
                  v-model="worksheetPrompt"
                  class="worksheet-prompt-textarea"
                  placeholder="Enter prompt..."
                />
              </div>
              <div
                v-else-if="uploadedImageUrl"
                class="image-preview-card has-image"
                role="button"
                tabindex="0"
                @click="handlePreviewArea"
                @keydown.enter.prevent="handlePreviewArea"
                @keydown.space.prevent="handlePreviewArea"
              >
                <img
                  :src="uploadedImageUrl"
                  alt="Worksheet preview"
                  class="image-preview-main"
                  @error="imageError = true"
                />
              </div>
              <div v-else class="no-worksheet-placeholder">
                <strong>No worksheet preview</strong>
                <span>Use Camera or Gallery to add one.</span>
              </div>
            </div>
          </div>
          <!-- Right column: Buttons -->
          <div class="image-upload-buttons-column">
            <div class="image-upload-buttons upload-buttons-row">
              <template v-if="!uploadedImageUrl">
                <button type="button" @click="openCamera" class="upload-btn camera-btn">
                  Camera
                </button>
                <button type="button" @click="openGallery" class="upload-btn gallery-btn">
                  Gallery
                </button>
              </template>
              <template v-else>
                <button 
                  @click="deletePhoto" 
                  class="upload-btn delete-btn"
                  type="button"
                >
                  Delete
                </button>
              </template>

              <button
                type="button"
                @click="toggleWorksheetPrompt"
                class="upload-btn btn-secondary"
              >
                Prompt
              </button>

              <button 
                type="button" 
                @click="buildEarworm" 
                class="action-btn construct-btn"
                v-if="canBuildEarworm"
                style="width: 100%;"
              >
                Build
              </button>
              <button
                v-if="isAudioAndTranscriptReady"
                type="button"
                @click="playEarworm"
                class="action-btn play-btn"
                style="width: 100%;"
              >
                Play
              </button>
              <input 
                ref="fileInput" 
                type="file" 
                accept="image/*"
                @change="handleFileSelect" 
                style="display: none;"
              />
            </div>
          </div>
          <div v-if="imageUploading" class="upload-status uploading-indicator">
            📤 Uploading image...
          </div>
        </div>



        <!-- Build Earworm Button: moved up, label removed -->

        <div class="earworm-status-section latest-status-row" style="margin-bottom: 1.5rem;">
          <template v-if="statusEvents.length">
            <div class="latest-status-label"><strong>Status</strong></div>
            <div class="status-events-list">
              <div
                v-for="(event, index) in statusEvents"
                :key="`${event.time}-${index}`"
                class="status-event-row"
              >
                <div class="status-event-text">{{ getDisplayText(event, statusEvents) }}</div>
              </div>
            </div>
          </template>
          <template v-else-if="earworm.status">
            <div class="latest-status-label"><strong>Status:</strong></div>
            <div class="latest-status-text">{{ typeof earworm.status === 'string' ? earworm.status : earworm.status?.lastevent?.text || 'In progress' }}</div>
          </template>
          <template v-else>
            <div class="latest-status-label"><strong>Status:</strong></div>
            <div class="latest-status-text">No status updates yet.</div>
          </template>
        </div>

        <div class="earworm-form-actions">
          <div class="earworm-form-actions-label"><strong>Earworm</strong></div>
          <div class="earworm-form-actions-buttons">
            <button type="button" @click="goBack" class="btn-secondary">Cancel</button>
            <button type="submit" :disabled="saving" class="btn-primary">
              {{ saving ? 'Saving...' : (isEditing ? 'Save' : 'Create Earworm') }}
            </button>
            <button 
              v-if="isEditing" 
              type="button" 
              @click="deleteEarworm" 
              class="btn-danger"
            >
              Delete
            </button>
          </div>
        </div>
      </form>
    </main>
    
    <!-- Task Details Display moved outside form but still in main -->
    <div v-if="taskDetails" class="task-details-display">
      <h3>Task Details</h3>
      <p><strong>Status:</strong> {{ taskDetails.status }}</p>
      
      <!-- Display multiple songs if they exist -->
      <div v-if="taskDetails.response?.data?.response?.sunoData" class="songs-list">
        <h4>Generated Songs ({{ taskDetails.response.data.response.sunoData.length }})</h4>
        <div 
          v-for="(song, index) in taskDetails.response.data.response.sunoData" 
          :key="index"
          class="song-item"
        >
          <div class="song-header">
        <h5>Song {{ Number(index) + 1 }}: {{ song.title || 'Untitled' }}</h5>
            <span class="song-duration">{{ song.duration }}s</span>
          </div>
          
          <div class="song-urls">
            <div v-if="song.audioUrl" class="url-item">
              <label>Audio URL:</label>
              <div class="url-controls">
                <input 
                  :value="song.audioUrl" 
                  readonly 
                  class="url-input"
                  :id="`audio-url-${index}`"
                />
                <button @click="copyToClipboard(song.audioUrl, `audio-url-${index}`)">Copy</button>
                <button @click="playAudio(song.audioUrl)">Play</button>
              </div>
            </div>
            
            <div v-if="song.imageUrl" class="url-item">
              <label>Image URL:</label>
              <div class="url-controls">
                <input 
                  :value="song.imageUrl" 
                  readonly 
                  class="url-input"
                  :id="`image-url-${index}`"
                />
                <button @click="copyToClipboard(song.imageUrl, `image-url-${index}`)">Copy</button>
                <button @click="openInNewTab(song.imageUrl)">View</button>
              </div>
            </div>
          </div>
          
          <div v-if="song.imageUrl" class="song-image">
			<img :src="song.imageUrl" :alt="`Song ${Number(index) + 1} cover`" />
          </div>
        </div>
      </div>
      
      <!-- Raw JSON display for debugging -->
      <details class="raw-data">
        <summary>Raw Response Data</summary>
        <pre>{{ JSON.stringify(taskDetails, null, 2) }}</pre>
      </details>
    </div>
    
    <!-- Analysis Results Modal -->
    <div v-if="showAnalysisModal" class="modal-overlay" @click="closeAnalysisModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ analysisError ? 'Analysis Failed' : 'Analysis Complete' }}</h3>
          <button @click="closeAnalysisModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Error State -->
          <div v-if="analysisError" class="error-state">
            <div class="error-icon">⚠️</div>
            <h4>Analysis Error</h4>
            <p class="error-message">{{ analysisError }}</p>
            <button @click="retryAnalysis" class="btn-retry" :disabled="analysisLoading">
              {{ analysisLoading ? 'Retrying...' : 'Try Again' }}
            </button>
          </div>
          
          <!-- Success State -->
          <div v-else-if="analysisResults">
            <!-- Suggested Name Section -->
            <div v-if="analysisResults?.topic_name" class="suggested-name-section">
              <h4>💡 Suggested Name</h4>
              <div class="suggested-name-box">
                <strong>{{ analysisResults.topic_name }}</strong>
                <button 
                  @click="applySuggestedName" 
                  class="btn-apply-name"
                  :disabled="earworm.name === analysisResults.topic_name"
                >
                  {{ earworm.name === analysisResults.topic_name ? '✅ Applied' : '📝 Use This Name' }}
                </button>
              </div>
            </div>

            <!-- Analysis Details -->
            <details class="analysis-details">
              <summary>📊 View Analysis Details</summary>
              <div class="analysis-preview">
                <pre>{{ JSON.stringify(analysisResults, null, 2) }}</pre>
              </div>
            </details>
          </div>
        </div>
        <div class="modal-footer">
          <button v-if="!analysisError" @click="goBackToEarworms" class="btn-primary">Go Back to Earworms</button>
          <button @click="closeAnalysisModal" class="btn-secondary">
            {{ analysisError ? 'Close' : 'Continue Editing' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Analysis Loading Overlay -->
    <div v-if="analysisLoading" class="loading-overlay">
      <div class="loading-content">
        <div class="spinner"></div>
        <p>Analyzing image with ChatGPT...</p>
      </div>
    </div>
    
    <!-- Image View Modal -->
    <div v-if="showImageModal" class="modal-overlay image-modal-overlay" @click="showImageModal = false">
      <div class="modal-content image-modal-content" @click.stop>
        <div class="modal-header">
          <h3>📷 Image View</h3>
          <button @click="showImageModal = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body image-modal-body">
          <div class="image-container-modal">
            <img 
              :src="uploadedImageUrl" 
              alt="Full size preview" 
              class="modal-image"
              @error="imageError = true"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Both Timestamped Lyrics Modal -->
    <div v-if="showBothTimestampedLyrics" class="modal-overlay" @click="showBothTimestampedLyrics = false">
      <div class="modal-content lyrics-modal" @click.stop>
        <div class="modal-header">
          <h3>⏰ Both Timestamped Lyrics</h3>
          <button @click="showBothTimestampedLyrics = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="lyrics-display">
            <div>
              <h4>Track 1</h4>
              <div class="timestamped-lyrics-content">
                <div 
                  v-for="(word, idx) in earworm.transcript?.alignedWords || []"
                  :key="'track1-' + idx"
                  class="timestamped-word"
                >
                  <span class="word">{{ word.word }}</span>
                  <span class="timestamp">{{ formatTime(word.start ?? 0) }} - {{ formatTime(word.end ?? 0) }}</span>
                </div>
              </div>
            </div>
            <div style="margin-top:2rem;">
              <h4>Track 2</h4>
              <div class="timestamped-lyrics-content">
                <div 
                  v-for="(word, idx) in earworm.transcript?.alignedWords || []"
                  :key="'track2-' + idx"
                  class="timestamped-word"
                >
                  <span class="word">{{ word.word }}</span>
                  <span class="timestamp">{{ formatTime(word.start ?? 0) }} - {{ formatTime(word.end ?? 0) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showBothTimestampedLyrics = false" class="btn-secondary">Close</button>
        </div>
      </div>
    </div>

  
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Header from '../components/Header.vue'
import type { Earworm } from '../services/database'
import type { EarwormStatus } from '../services/database'
import type { EarwormEvent } from '../services/database'

const router = useRouter()
const route = useRoute()

const currentUser = ref<any>(null)
const saving = ref(false)
const imageError = ref(false)
const imageUploading = ref(false)
const uploadedImageUrl = ref('')
const analysisResults = ref<any>(null)
const showAnalysisModal = ref(false)
const analysisLoading = ref(false)
const lyricsLoading = ref(false)
const musicLoading = ref(false)
const analysisError = ref('')
const lastAnalyzedImageUrl = ref('')
const cameraOpen = ref(false)
const videoEl = ref<HTMLVideoElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)

// Add missing modal reactive variables
const showImageModal = ref(false)
const showBothTimestampedLyrics = ref(false);
const showCopyTimestampedLyricsModal = ref(false)
const showWorksheetSection = ref(true)

const worksheetPreviewMode = ref<'image' | 'prompt'>('image')
const worksheetPrompt = ref('')

const canBuildEarworm = computed(() => {
  const hasImage = !!uploadedImageUrl.value || !!earworm.image_url
  const hasPrompt = worksheetPrompt.value.trim().length > 0
  return hasImage || hasPrompt
})

const toggleWorksheetPrompt = () => {
  worksheetPreviewMode.value = worksheetPreviewMode.value === 'prompt' ? 'image' : 'prompt'
}

const formatTime = (seconds: number): string => {
  if (!seconds && seconds !== 0) return ''
  const mins = Math.floor(seconds / 60)
  const secs = (seconds % 60).toFixed(1)
  return `${mins}:${secs.padStart(4, '0')}`
}

// Task details variables
const loadingTaskDetails = ref(false)
const taskDetails = ref<any>(null)

// Suno status polling
const sunoStatus = ref('')
const sunoStatusMessage = ref('')
const isGeneratingMusic = ref(false)
const sunoAudioUrl = ref('')
let statusPollingInterval: number | null = null
const POLL_INTERVAL_MS = 3000

let mediaStream: MediaStream | null = null

const isEditing = computed(() => !!route.params.id)

// Update the earworm object to match your DB schema
const earworm = reactive<Earworm>({
  id: null as any,
  user_id: null as any,
  name: '',
  image_url: null,
  data: null,
  analysis: null,
  lyrics: null,
  audio_url: null,
  transcript: null,
  status: null,
  created_at: null,
  modified_at: null
})

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'

// Computed property to check for additional analysis data
const hasAdditionalAnalysisData = computed(() => {
  if (!earworm.analysis) return false
  const knownKeys = ['topic_name', 'vocabulary_pairs', 'analyzed_at', 'image_url']
  return Object.keys(earworm.analysis).some(key => !knownKeys.includes(key))
})

// Computed property to check if both audio and transcript are ready
const isAudioAndTranscriptReady = computed(() => {
  return earworm.audio_url && 
         earworm.transcript && 
         earworm.transcript.alignedWords && 
         earworm.transcript.alignedWords.length > 0
})

const parseStatusValue = (rawStatus: any): string | EarwormStatus | null => {
  if (!rawStatus) return null

  if (typeof rawStatus === 'string') {
    try {
      const parsed = JSON.parse(rawStatus)
      if (parsed && typeof parsed === 'object') {
        if (!Array.isArray(parsed.events)) parsed.events = []
        return parsed as EarwormStatus
      }
    } catch {
      return rawStatus
    }
  }

  if (typeof rawStatus === 'object') {
    if (!Array.isArray((rawStatus as EarwormStatus).events)) {
      (rawStatus as EarwormStatus).events = []
    }
    return rawStatus as EarwormStatus
  }

  return rawStatus
}

const deriveStatusFromData = (data: Partial<Earworm>): string => {
  if (data.audio_url) return 'tracks complete'
  if (data.lyrics) return 'lyrics complete'
  if (data.analysis) return 'analysis complete'
  return 'draft'
}

const getLatestStatusText = (statusValue: string | EarwormStatus | null | undefined): string => {
  if (!statusValue) return ''
  if (typeof statusValue === 'string') return statusValue
  if (statusValue?.lastevent?.text) return statusValue.lastevent.text
  return ''
}

const formatStatusEventTime = (timeValue: string | number) => {
  if (!timeValue && timeValue !== 0) return ''
  const date = typeof timeValue === 'number' ? new Date(timeValue) : new Date(timeValue)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
}

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return `${mins}m ${secs}s`
}

const statusEvents = computed<EarwormEvent[]>(() => {
  const statusValue = earworm.status
  if (statusValue && typeof statusValue === 'object' && Array.isArray((statusValue as EarwormStatus).events)) {
    return [...(statusValue as EarwormStatus).events].sort((a, b) => {
      const timeA = new Date(a.time).getTime()
      const timeB = new Date(b.time).getTime()
      return timeB - timeA
    })
  }
  return []
})

const startTime = computed(() => {
  if (statusEvents.value.length === 0) return null
  const times = statusEvents.value.map(e => new Date(e.time).getTime())
  return Math.min(...times)
})

const getStepStartTime = (eventText: string, eventTime: string | number, allEvents: EarwormEvent[]) => {
  const globalStart = startTime.value
  if (eventText.includes('analyzeImage')) return globalStart
  if (eventText.includes('buildLyrics')) return globalStart
  if (eventText.includes('generateAudio started')) return new Date(eventTime).getTime()
  if (eventText.includes('generateAudio complete')) {
    const startedEvent = allEvents.find(e => e.text.includes('generateAudio started'))
    return startedEvent ? new Date(startedEvent.time).getTime() : globalStart
  }
  if (eventText.includes('generateTranscript')) {
    const transcriptEvents = allEvents.filter(e => e.text.includes('generateTranscript'))
    const firstTranscript = transcriptEvents[0]
    return firstTranscript ? new Date(firstTranscript.time).getTime() : globalStart
  }
  return globalStart
}

const getDisplayText = (event: EarwormEvent, allEvents: EarwormEvent[]) => {
  const stepStart = getStepStartTime(event.text, event.time, allEvents)
  if (!stepStart) return event.text
  const elapsed = Math.round((new Date(event.time).getTime() - stepStart) / 1000)
  const elapsedStr = formatDuration(elapsed)

  if (event.text.includes('analyzeImage complete')) {
    return `analysis complete (${elapsedStr})`
  }
  if (event.text.includes('buildLyrics complete')) {
    return `lyrics complete (${elapsedStr})`
  }
  if (event.text.includes('generateAudio started')) {
    return `generating audio (0s)`
  }
  if (event.text.includes('generateAudio complete')) {
    return `audio complete (${elapsedStr})`
  }
  if (event.text.includes('generateTranscript complete')) {
    return `transcript complete (${elapsedStr})`
  }
  if (event.text.includes('generateAudio failed')) {
    return `audio failed (${elapsedStr})`
  }
  return event.text
}

const stopStatusPolling = () => {
  if (statusPollingInterval !== null) {
    clearInterval(statusPollingInterval)
    statusPollingInterval = null
  }
}

const applyEarwormResponse = (data: any) => {
  earworm.name = data.name || earworm.name || 'Untitled Earworm'
  earworm.user_id = data.user_id ?? null
  earworm.image_url = data.image_url ?? null
  earworm.data = data.data ?? earworm.data ?? null

  // Hydrate prompt UI from stored JSON data (preferred)
  if (data.data && typeof data.data === 'object') {
    const storedPrompt = (data.data as any).prompt
    if (typeof storedPrompt === 'string') worksheetPrompt.value = storedPrompt
  } else if (data.analysis && typeof data.analysis === 'object') {
    // Back-compat fallback (older records)
    const storedPrompt = (data.analysis as any).prompt
    if (typeof storedPrompt === 'string') worksheetPrompt.value = storedPrompt
  }
  if (data.image_url !== undefined) {
    uploadedImageUrl.value = data.image_url || ''
  }
  earworm.analysis = data.analysis ?? null
  earworm.lyrics = data.lyrics ?? null
  earworm.audio_url = data.audio_url ?? null
  earworm.transcript = data.transcript ?? null
  earworm.created_at = data.created_at ?? null
  earworm.modified_at = data.modified_at ?? null

  if (data.status !== undefined) {
    const parsedStatus = parseStatusValue(data.status)
    earworm.status = parsedStatus ?? deriveStatusFromData(data)
  }
}

const refreshEarwormFromServer = async () => {
  if (!earworm.id) return

  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }

    const response = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}`, { headers })
    if (!response.ok) {
      console.warn('Status poll failed with status', response.status)
      return
    }

    const data = await response.json()
    
    // Only update fields that change during build process, not user-editable fields like name
    if (data.status !== undefined) {
      const parsedStatus = parseStatusValue(data.status)
      earworm.status = parsedStatus ?? deriveStatusFromData(data)
    }
    earworm.lyrics = data.lyrics ?? earworm.lyrics
    earworm.audio_url = data.audio_url ?? earworm.audio_url
    earworm.transcript = data.transcript ?? earworm.transcript
  } catch (error) {
    console.error('❌ Failed to refresh earworm:', error)
  }
}

const startStatusPolling = () => {
  if (statusPollingInterval !== null) return
  void refreshEarwormFromServer()
  statusPollingInterval = window.setInterval(() => {
    void refreshEarwormFromServer()
  }, POLL_INTERVAL_MS)
}

// Utility functions for modals
const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleString()
  } catch {
    return dateString
  }
}

const playEarworm = () => {
  if (earworm.id) {
    console.log('🎵 Playing earworm from builder:', earworm.id)
    router.push(`/earworm-player/${earworm.id}`)
  }
}

const openImageInNewTab = () => {
  if (uploadedImageUrl.value) {
    window.open(uploadedImageUrl.value, '_blank')
  }
}

const copyImageUrlToClipboard = async () => {
  if (!uploadedImageUrl.value) return
  
  try {
    await navigator.clipboard.writeText(uploadedImageUrl.value)
    console.log('✅ Image URL copied to clipboard')
    
    // Show brief feedback
    const buttons = document.querySelectorAll('.btn-secondary')
    const copyButton = Array.from(buttons).find(btn => btn.textContent?.includes('Copy URL')) as HTMLElement
    if (copyButton) {
      const originalText = copyButton.textContent
      copyButton.textContent = '✅ Copied!'
      setTimeout(() => {
        copyButton.textContent = originalText
      }, 2000)
    }
  } catch (error) {
    console.error('❌ Failed to copy image URL:', error)
  }
}

// Complete the uploadImageToCloudinary function
const uploadImageToCloudinary = async (dataUrl: string) => {
  imageUploading.value = true
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }

    // If we don't have an earworm ID yet, create the earworm first
    if (!earworm.id) {
      console.log('Creating earworm record before image upload...')
      await createEarwormRecord()
    }
    
    const response = await fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ image: dataUrl })
    })
    
    if (!response.ok) {
      if (response.status === 413) {
        throw new Error('Image file is too large. Please try a smaller image or compress it further.')
      }
      throw new Error(`Upload failed with status ${response.status}`)
    }
    
    const result = await response.json()
    uploadedImageUrl.value = result.url
    earworm.image_url = result.url
    console.log('Image uploaded successfully:', result.url)
    
    // Update the earworm record with the image URL
    if (earworm.id) {
      await updateEarwormImage(earworm.id, result.url)
    }
    
    console.log('✅ Image uploaded and saved to earworm record')
    
  } catch (error) {
    console.error('Image upload failed:', error)
    alert('Failed to upload image: ' + (error instanceof Error ? error.message : 'Unknown error'))
  } finally {
    imageUploading.value = false
  }
}

// Add missing functions that are referenced in the template
const deletePhoto = async () => {
  if (!confirm('Are you sure you want to delete this photo? This will also clear the analysis results.')) {
    return
  }
  
  try {
    console.log('🗑️ Deleting photo and clearing analysis')
    
    // Clear the uploaded image
    uploadedImageUrl.value = ''
    imageError.value = false
    
    // Clear analysis results
    const previousAnalysis = analysisResults.value
    analysisResults.value = null
    analysisError.value = ''
    showAnalysisModal.value = false
    lastAnalyzedImageUrl.value = ''
    
    // Clear earworm stored fields tied to image
    earworm.analysis = null
    earworm.image_url = null
    
    // Reset name to default if it was likely auto-generated from analysis
    if (previousAnalysis?.topic_name && earworm.name === previousAnalysis.topic_name) {
      earworm.name = 'Untitled Earworm'
      console.log('🏷️ Reset name to default since photo was deleted')
    }
    
    // If editing an existing earworm, update the database to remove the image
    if (earworm.id) {
      await updateEarwormImage(earworm.id, null)
      console.log('✅ Removed image from database')
    }
    
    console.log('✅ Photo deleted successfully')
    
  } catch (error) {
    console.error('❌ Error deleting photo:', error)
    alert('Failed to delete photo')
  }
}

const analyzeImageWithChatGPT = async (imageUrl: string) => {
  // Prevent duplicate requests for the same image
  if (analysisLoading.value || lastAnalyzedImageUrl.value === imageUrl) {
    console.log('🚫 Skipping duplicate analysis request for:', imageUrl)
    return
  }
  
  analysisLoading.value = true
  analysisError.value = ''
  lastAnalyzedImageUrl.value = imageUrl
  
  try {
    console.log('🔍 Starting ChatGPT analysis for image:', imageUrl)
    
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const requestBody = {
      use_default_prompt: true,
      image_url: imageUrl,
      earworm_id: earworm.id
    }
    
    const response = await fetch(`${API_BASE_URL}/api/chatgpt/analyze-doc`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${data.details || 'Unknown error'}`)
    }
    
    console.log('✅ ChatGPT analysis completed successfully')
    analysisResults.value = data.response
    
    // Update earworm analysis
    earworm.analysis = data.response
    
  } catch (error) {
    console.error('❌ ChatGPT analysis failed:', error)
    
    let errorMessage = 'Failed to analyze image with ChatGPT'
    if (error instanceof Error) {
      if (error.message.includes('Rate limit') || error.message.includes('429')) {
        errorMessage = 'Rate limit exceeded. Please wait a few minutes and try again.'
      } else {
        errorMessage = error.message
      }
    }
    
    analysisError.value = errorMessage
    showAnalysisModal.value = true
    
  } finally {
    analysisLoading.value = false
  }
}

const retryAnalysis = async () => {
  if (uploadedImageUrl.value) {
    lastAnalyzedImageUrl.value = ''
    await analyzeImageWithChatGPT(uploadedImageUrl.value)
  }
}

const closeAnalysisModal = () => {
  showAnalysisModal.value = false
  analysisError.value = ''
}

const applySuggestedName = () => {
  if (analysisResults.value?.topic_name) {
    earworm.name = analysisResults.value.topic_name
    console.log('✅ Applied suggested name:', earworm.name)
  }
}

const goBackToEarworms = () => {
  router.push({ name: 'earworms' })
}

const handleGenerateLyrics = async () => {
  try {
    lyricsLoading.value = true
    
    if (!earworm.id) {
      console.warn('Earworm not saved yet. Please save the earworm first.')
      return
    }
    
    console.log('🎵 Starting lyrics generation for earworm:', earworm.id)
    
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const response = await fetch(`${API_BASE_URL}/api/chatgpt/generate-lyrics`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        earworm_id: earworm.id
      })
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP ${response.status}: Failed to generate lyrics`)
    }
    
    const data = await response.json()
    console.log('✅ Lyrics generated successfully')
    
    // Update earworm lyrics
    earworm.lyrics = data.lyrics_data.lyrics
    
  } catch (error) {
    console.error('❌ Error generating lyrics:', error)
    alert('Failed to generate lyrics: ' + (error instanceof Error ? error.message : 'Unknown error'))
  } finally {
    lyricsLoading.value = false
  }
}

const handleGenerateMusic = async () => {
  try {
    musicLoading.value = true
    
    if (!earworm.id) {
      console.warn('Earworm not saved yet. Please save the earworm first.')
      return
    }
    
    console.log('🎶 Starting music generation for earworm:', earworm.id)
    
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}/generate-music`, {
      method: 'POST',
      headers
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP ${response.status}: Failed to generate music`)
    }
    
    const musicData = await response.json()
    console.log('✅ Music generation started successfully:', musicData)
    
  } catch (error) {
    console.error('❌ Error generating music:', error)
    alert('Failed to generate music: ' + (error instanceof Error ? error.message : 'Unknown error'))
  } finally {
    musicLoading.value = false
  }
}

const createEarwormRecord = async () => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const payload = {
      name: earworm.name,
      user_id: currentUser.value?.id || null,
      image_url: uploadedImageUrl.value || null,
      data: {
        ...(earworm.data && typeof earworm.data === 'object' ? earworm.data : {}),
        prompt: worksheetPrompt.value
      },
      analysis: earworm.analysis,
      lyrics: earworm.lyrics,
      audio_url: earworm.audio_url,
      status: earworm.status
      // ...add/remove fields as needed per your schema...
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })
    
    if (!response.ok) {
      throw new Error('Failed to create earworm')
    }
    
    const result = await response.json()
    earworm.id = result.id
    console.log('Created earworm record with ID:', result.id)
    
    return result
  } catch (error) {
    console.error('Failed to create earworm record:', error)
    throw error
  }
}

const updateEarwormImage = async (earwormId: string | number, imageUrl: string | null) => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const idString = String(earwormId)
    
    const fetchResponse = await fetch(`${API_BASE_URL}/api/earworms/${idString}`, { headers })
    if (!fetchResponse.ok) {
      throw new Error('Failed to fetch earworm data')
    }
    
    const currentData = await fetchResponse.json()
    
    const updatePayload = {
      name: currentData.name,
      user_id: currentData.user_id,
      image_url: imageUrl,
      data: {
        ...(currentData.data && typeof currentData.data === 'object' ? currentData.data : {}),
        prompt: worksheetPrompt.value
      },
      analysis: currentData.analysis,
      lyrics: currentData.lyrics,
      audio_url: currentData.audio_url,
      status: currentData.status
      // ...add/remove fields as needed per your schema...
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms/${idString}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updatePayload)
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error('Failed to update earworm with image: ' + errorText)
    }
    
    const result = await response.json()
    console.log('Earworm updated successfully:', result)
  } catch (error) {
    console.error('Failed to update earworm with image:', error)
  }
}

const updateEarwormStatus = async (earwormId: string | number, status: any) => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const idString = String(earwormId)
    
    const fetchResponse = await fetch(`${API_BASE_URL}/api/earworms/${idString}`, { headers })
    if (!fetchResponse.ok) {
      throw new Error('Failed to fetch earworm data')
    }
    
    const currentData = await fetchResponse.json()
    
    const updatePayload = {
      name: currentData.name,
      user_id: currentData.user_id,
      image_url: currentData.image_url,
      data: {
        ...(currentData.data && typeof currentData.data === 'object' ? currentData.data : {}),
        prompt: worksheetPrompt.value
      },
      analysis: currentData.analysis,
      lyrics: currentData.lyrics,
      audio_url: currentData.audio_url,
      status: status
      // ...add/remove fields as needed per your schema...
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms/${idString}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updatePayload)
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error('Failed to update earworm status: ' + errorText)
    }
    
    const result = await response.json()
    console.log('Earworm status updated successfully:', result)
  } catch (error) {
    console.error('Failed to update earworm status:', error)
  }
}


// Add missing utility functions for task details
const copyToClipboard = async (text: string, inputId: string) => {
  try {
    await navigator.clipboard.writeText(text)
    console.log('✅ Copied to clipboard:', text)
    const input = document.getElementById(inputId) as HTMLInputElement
    if (input) {
      input.select()
      input.style.background = '#d4edda'
      setTimeout(() => {
        input.style.background = '#f8f9fa'
      }, 1000)
    }
  } catch (error) {
    console.error('❌ Failed to copy to clipboard:', error)
    const input = document.getElementById(inputId) as HTMLInputElement
    if (input) {
      input.select()
    }
  }
}


const playAudio = (audioUrl: string) => {
  const audio = new Audio(audioUrl)
  audio.play().catch(error => {
    console.error('❌ Failed to play audio:', error)
  })
}

const openInNewTab = (url: string) => {
  window.open(url, '_blank')
}

// Add missing camera functions
const openCamera = async () => {
  try {
    cameraOpen.value = true
    mediaStream = await navigator.mediaDevices.getUserMedia({ 
      video: { 
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 1655 }
      } 
    })
    if (videoEl.value) {
      videoEl.value.srcObject = mediaStream
    }
  } catch (err) {
    console.error('Camera access error:', err)
    alert('Unable to access camera')
    cameraOpen.value = false
  }
}

const closeCamera = () => {
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop())
    mediaStream = null
  }
  if (videoEl.value) {
    videoEl.value.srcObject = null
  }
  cameraOpen.value = false
}

const capturePhoto = () => {
  const video = videoEl.value
  if (!video) return
  
  const canvas = document.createElement('canvas')
  const srcWidth = video.videoWidth || 640
  const srcHeight = video.videoHeight || 480
  
  const documentAspectRatio = 8.5 / 11
  let cropWidth = srcWidth
  let cropHeight = srcHeight
  let cropX = 0
  let cropY = 0
  
  const currentAspectRatio = srcWidth / srcHeight
  
  if (currentAspectRatio > documentAspectRatio) {
    cropWidth = srcHeight * documentAspectRatio
    cropX = (srcWidth - cropWidth) / 2
  } else {
    cropHeight = srcWidth / documentAspectRatio
    cropY = (srcHeight - cropHeight) / 2
  }
  
  const maxWidth = 640
  const targetWidth = maxWidth
  const targetHeight = maxWidth / documentAspectRatio
  
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.drawImage(video, cropX, cropY, cropWidth, cropHeight, 0, 0, targetWidth, targetHeight)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
    uploadImageToCloudinary(dataUrl)
  }
  
  closeCamera()
}

const openGallery = () => {
  if (fileInput.value) {
    fileInput.value.removeAttribute('capture')
    fileInput.value.setAttribute('accept', 'image/*')
    fileInput.value.click()
  }
}

const handlePreviewArea = () => {
  if (uploadedImageUrl.value) {
    showImageModal.value = true
  } else {
    openGallery()
  }
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    compressAndUploadImage(file)
  }
}

const compressAndUploadImage = (file: File) => {
  const reader = new FileReader()
  reader.onload = (e) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      
      const maxWidth = 1200
      let { width, height } = img
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }
      
      canvas.width = width
      canvas.height = height
      ctx.drawImage(img, 0, 0, width, height)
      const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8)
      
      uploadImageToCloudinary(compressedDataUrl)
    }
    img.src = e.target?.result as string
  }
  reader.readAsDataURL(file)
}

// Lifecycle hooks
onMounted(async () => {
  const userStr = sessionStorage.getItem('currentUser')
  if (userStr) {
    currentUser.value = JSON.parse(userStr)
  }
    
  if (isEditing.value) {
    const earwormId = route.params.id as string
    console.log('📝 Editing mode - earworm ID from route:', earwormId, typeof earwormId)
    
    // Validate the ID before proceeding
    if (!earwormId || earwormId === 'undefined' || earwormId === 'null') {
      console.error('❌ Invalid earworm ID in route params:', earwormId)
      router.push({ name: 'earworms' })
      return
    }
    
    // Set the earworm ID as string (server expects string UUIDs)
    earworm.id = earwormId
    await fetchEarwormDetails(earwormId)
  }
  
  // Debug logging
  console.log('[EarwormBuilder] onMounted: earworm.transcript:', earworm.transcript)
  showImageModal.value = false // Ensure modal is closed on mount
})

// Remove unnecessary type checking for status and EarwormStatus/EarwormEvent
const getStepStatus = (step: string) => {
  let statusText: string | undefined

  const latestEvent = earworm.status ? (earworm.status as any).lastevent : null
  if (latestEvent && typeof latestEvent === 'object' && latestEvent.text) {
    statusText = latestEvent.text
  } else {
    statusText = earworm.status as any
  }

  if (!statusText || statusText === 'draft') {
    return 'pending'
  }

  const stepOrder = ['analyzing worksheet', 'writing lyrics', 'generating tracks']
  const currentStepIndex = stepOrder.findIndex(s => statusText!.includes(s))
  const targetStepIndex = stepOrder.findIndex(s => step === s)

  if (statusText.includes('failed')) {
    if (targetStepIndex <= currentStepIndex) {
      return 'failed'
    }
    return 'pending'
  }

  if (statusText === 'tracks complete') {
    return 'completed'
  }

  if (targetStepIndex < currentStepIndex) {
    return 'completed'
  } else if (targetStepIndex === currentStepIndex) {
    return 'active'
  } else {
    return 'pending'
  }
}


// When fetching earworm details, ensure status is parsed as object if possible
const fetchEarwormDetails = async (earwormId: string) => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }

    // Validate earwormId
    if (!earwormId || earwormId === 'undefined' || earwormId === 'null') {
      throw new Error('Invalid earworm ID provided')
    }

    console.log('🔍 Fetching earworm details for ID:', earwormId)

    const response = await fetch(`${API_BASE_URL}/api/earworms/${earwormId}`, { headers })

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Earworm not found')
      }
      throw new Error(`Server error: ${response.status}`)
    }

    const data = await response.json()
    console.log('📡 Received earworm data:', data)

    applyEarwormResponse(data)

    // Debug logging
    console.log('[EarwormBuilder] fetchEarwormDetails: transcript:', data.transcript)
    console.log('[EarwormBuilder] after assignment: earworm.transcript:', earworm.transcript)
    console.log('✅ Earworm data loaded successfully with status:', earworm.status)

    // Ensure we are polling for status updates
    startStatusPolling()
  } catch (error) {
    console.error('❌ Failed to load earworm:', error)
    alert(`Failed to load earworm: ${error instanceof Error ? error.message : 'Unknown error'}`)
    router.push({ name: 'earworms' })
  }
}

// Add the missing deleteEarworm function for Delete button
const deleteEarworm = async () => {
  if (!earworm.id) return
  if (!confirm('Are you sure you want to delete this earworm? This cannot be undone.')) {
    return
  }
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    const response = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}`, {
      method: 'DELETE',
      headers
    })
    if (!response.ok) {
      throw new Error('Failed to delete earworm')
    }
    router.push({ name: 'earworms' })
  } catch (error) {
    console.error('Failed to delete earworm:', error)
    alert('Failed to delete earworm')
  }
}

// Remove duplicate getFailureMessage if present
// ...existing code...

const goBack = () => {
  router.push({ name: 'earworms' })
}


// Add the missing buildEarworm function
const building = ref(false)

const buildEarworm = async () => {
  if (!canBuildEarworm.value) {
    alert('Please add a worksheet image or enter a prompt first.')
    return
  }
  
  building.value = true
  
  try {
    console.log('🏗️ Starting earworm build process')
    
    // Ensure we have an earworm record
    if (!earworm.id) {
      await createEarwormRecord()
    } else {
      // Persist the latest prompt/image_url before building so the server can decide
      // whether to use prompt-based analysis or image analysis.
      const userStr = sessionStorage.getItem('currentUser')
      const userData = userStr ? JSON.parse(userStr) : null
      const sessionToken = userData?.sessionToken
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      }
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`
      }

      const payload = {
        name: earworm.name,
        user_id: earworm.user_id,
        image_url: uploadedImageUrl.value || earworm.image_url,
        data: {
          ...(earworm.data && typeof earworm.data === 'object' ? earworm.data : {}),
          prompt: worksheetPrompt.value
        },
        analysis: earworm.analysis,
        lyrics: earworm.lyrics,
        audio_url: earworm.audio_url,
        status: earworm.status
      }

      const saveResponse = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      })

      if (!saveResponse.ok) {
        const errorText = await saveResponse.text()
        throw new Error('Failed to save earworm before build: ' + errorText)
      }
    }
    
    // Clear previous events and set initial status
    if (earworm.status && typeof earworm.status === 'object' && (earworm.status as any).events) {
      (earworm.status as any).events = []
    }
    earworm.status = 'analyzing worksheet'
    
    // Update the earworm record with cleared events
    await updateEarwormStatus(earworm.id, earworm.status)
    
    startStatusPolling()
    
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}/build`, {
      method: 'POST',
      headers
    })
    
    if (!response.ok) { 
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to start build process')
    }
    
    console.log('✅ Build process started successfully')
    
    
  } catch (error) {
    console.error('❌ Error building earworm:', error)
    alert('Failed to build earworm: ' + (error instanceof Error ? error.message : 'Unknown error'))
    earworm.status = 'draft'
    startStatusPolling()
  } finally {
    building.value = false
  }
}

// Add this function to create a new earworm and redirect to builder with the new id
const createAndGoToNewEarworm = async () => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }

    // Create a default earworm record
    const payload = {
      name: 'Untitled Earworm',
      user_id: userData?.id || null,
      image_url: null,
      data: { prompt: worksheetPrompt.value },
      analysis: null,
      lyrics: null,
      audio_url: null,
      transcript: {},
      status: {}
    }

    const response = await fetch(`${API_BASE_URL}/api/earworms`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      throw new Error('Failed to create new earworm')
    }

    const result = await response.json()
    // Redirect to builder with new id
    router.push({ name: 'earworm-builder', params: { id: result.id } })
  } catch (error) {
    alert('Failed to create new earworm: ' + (error instanceof Error ? error.message : 'Unknown error'))
  }
}

// Add the missing saveEarworm function for form submission
const saveEarworm = async () => {
  saving.value = true
  try {
    // If creating new, call createEarwormRecord, else update existing
    if (!earworm.id) {
      await createEarwormRecord()
      alert('Earworm created successfully!')
    } else {
      // Update existing earworm
      const userStr = sessionStorage.getItem('currentUser')
      const userData = userStr ? JSON.parse(userStr) : null
      const sessionToken = userData?.sessionToken
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      }
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`
      }
      const payload = {
        name: earworm.name,
        user_id: earworm.user_id,
        image_url: uploadedImageUrl.value || earworm.image_url,
        data: {
          ...(earworm.data && typeof earworm.data === 'object' ? earworm.data : {}),
          prompt: worksheetPrompt.value
        },
        analysis: earworm.analysis,
        lyrics: earworm.lyrics,
        audio_url: earworm.audio_url,
        status: earworm.status
      }
      const response = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      })
      if (!response.ok) {
        throw new Error('Failed to update earworm')
      }
      alert('Earworm saved successfully!')
    }
    router.push({ name: 'earworms' })
  } catch (error) {
    alert('Failed to save earworm: ' + (error instanceof Error ? error.message : 'Unknown error'))
  } finally {
    saving.value = false
  }
}

onUnmounted(() => {
  stopStatusPolling()
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop())
  }
})
</script>

<style scoped>
.earworm-builder-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.page-main {
  padding: 2rem 0;
}

.form-section {
  margin-bottom: 2.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.upload-form-group {
  margin-bottom: 1.5rem;
}

.earworm-upload-section {
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.form-help {
  color: #6c757d;
  font-size: 0.9rem;
}

.worksheet-toggle-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.25rem;
}

.worksheet-label {
  font-weight: 600;
  color: #343a40;
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
   font-weight: 500;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.camera-btn {
  background: #007bff;
  color: white;
}

.camera-btn:hover {
  background: #0056b3;
}

.gallery-btn {
  background: #28a745;
  color: white;
}

.gallery-btn:hover {
  background: #218838;
}

.image-upload-section {
  display: flex;
  gap: 1.5rem;
  align-items: stretch;
  flex-wrap: wrap;
}

.image-preview-column {
  flex: 0 0 60%;
}

.image-upload-buttons-column {
  flex: 0 0 40%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.image-preview-area {
  min-height: 260px;
  display: flex;
  align-items: stretch;
}

.image-preview-card {
  flex: 1 1 100%;
  min-height: 260px;
  border: 2px dashed #ced4da;
  border-radius: 12px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
}

.image-preview-card.has-image {
  border-style: solid;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.image-preview-card:focus {
  outline: none;
  border-color: #43a047;
  box-shadow: 0 0 0 3px rgba(67, 160, 71, 0.25);
}

.prompt-preview-card {
  cursor: default;
}

.worksheet-prompt-textarea {
  width: 100%;
  min-height: 220px;
  height: 100%;
  border: 1px solid #ced4da;
  border-radius: 10px;
  padding: 0.75rem;
  font: inherit;
  resize: vertical;
  background: white;
}

.no-worksheet-placeholder {
  flex: 1 1 100%;
  border: 2px dashed #ced4da;
  border-radius: 12px;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  color: #6c757d;
  text-align: center;
  line-height: 1.5;
}

.no-worksheet-placeholder strong {
  color: #343a40;
  margin-bottom: 0.35rem;
}

.image-preview-main {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.image-preview {
  margin-top: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}



.preview-image {
  width: 100%;
  height: auto;
  display: block;
}

.delete-photo-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 0.5rem;
  transition: background-color 0.2s;
}

.delete-photo-btn:hover {
  background: rgba(255, 255, 255, 0.6);
}

.image-overlay-hint {
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.audio-players {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.audio-player-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner-small {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 800px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0,   0,  0, 0, 0.1);
}

.image-modal-content {
  padding: 0;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  color: #333;
  background: rgba(0, 0, 0, 0.1);
}

.modal-body {
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
  justify-content: flex-end;
   flex-shrink: 0;
}

.analysis-display,
.lyrics-display {
  height: 100%;
  overflow-y: auto;
}

.analysis-section {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.analysis-section:last-child {
   border-bottom: none;
  margin-bottom: 0;
}

.analysis-section h4 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.3rem;
  font-weight: 600;
}

.topic-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2196f3;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
  margin-bottom: 1rem;
}

.vocabulary-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.vocabulary-pair {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.english-term {
  flex: 1;
  font-weight: 500;
  color: #495057;
  font-size: 1.1rem;
}

.arrow {
  color: #6c757d;
  font-weight: bold;
  font-size: 1.2rem;
}

.spanish-term {
  flex: 1;
  font-weight: 600;
  color: #28a745;
  font-size: 1.1rem;
}

.additional-data {
  margin-top: 1rem;
}

.additional-data summary {
  cursor: pointer;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  font-weight: 500;
  font-size: 1.1rem;
}

.additional-data summary:hover {
  background: #e9ecef;
}

.analysis-raw {
   background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 1.5rem;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  line-height: 1.4;
  white-space: pre-wrap;
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
}

.analysis-metadata {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
  text-align: center;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
  font-size: 1rem;
}

.no-analysis,
.no-lyrics {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  font-size: 1.2rem;
}

.lyrics-content {
  margin-bottom: 2rem;
}

.lyrics-text {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 2rem;
  font-family: inherit;
  font-size: 1.1rem;
  line-height: 1.8;
  white-space: pre-wrap;
  margin: 0;
  color: #333;
  min-height: 300px;
}

.lyrics-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.btn-copy-lyrics {
  background: #17a2b8;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.btn-copy-lyrics:hover {
  background: #138496;
}

.btn-play-with-lyrics {
  background: #28a745;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.btn-play-with-lyrics:hover {
  background: #218838;
}

.btn-generate-lyrics {
  background: #28a745;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.btn-generate-lyrics:hover {
  background: #218838;
}

/* Build Process Styles */
.build-section {
  text-align: center;
  padding: 2rem;
  border: 2px dashed #e9ecef;
  border-radius: 12px;
  background: #f8f9fa;
}

.build-description {
  font-size: 1.1rem;
  color: #495057;
  margin-bottom: 1rem;
}

.build-steps-list {
  text-align: left;
  max-width: 400px;
  margin: 1.5rem auto;
  padding-left: 0;
  list-style: none;
}

.build-steps-list li {
  padding: 0.5rem 0;
  font-size: 1rem;
  color: #495057;
}


/* Build Status Styles */
.build-status {
  padding: 1.5rem;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: white;
}

.status-steps {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  position: relative;
}

.status-steps::before {
  content: '';
  position: absolute;
  top: 30px;
  left: 60px;
  right: 60px;
  height: 2px;
  background: #e9ecef;
  z-index: 1;
}

.status-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  flex: 1;
}

.step-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  position: relative;
  background: #f8f9fa;
  border: 3px solid #e9ecef;
  transition: all 0.3s ease;
}

.step-text {
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  color: #6c757d;
}

.step-spinner {
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  border: 3px solid transparent;
  border-top: 3px solid #43a047;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.status-step.pending .step-icon {
  background: #f8f9fa;
  border-color: #e9ecef;
}

.status-step.active .step-icon {
  background: #e3f2fd;
  border-color: #43a047;
  color: #43a047;
}

.status-step.active .step-text {
  color: #43a047;
  font-weight: 600;
}

.status-step.completed .step-icon {
  background: #e8f5e8;
  border-color: #43a047;
  color: #43a047;
}

.status-step.completed .step-text {
  color: #43a047;
}

.status-step.failed .step-icon {
  background: #ffebee;
  border-color: #f44336;
  color: #f44336;
}

.status-step.failed .step-text {
  color: #f44336;
}

.status-message {
  text-align: center;
  margin-bottom: 1.5rem;
}

.status-text {
  font-size: 1.1rem;
  padding: 1rem;
  border-radius: 8px;
  background: #f8f9fa;
  color: #495057;
}

.status-events-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
}

.status-event-row {
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.status-event-text {
  font-weight: 500;
  color: #343a40;
  font-size: 0.85rem;
}

.status-event-time {
  color: #6c757d;
}

.status-text.success {
  background: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.status-text.error {
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
}

.build-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.delete-btn {
  background: #dc3545;
  color: white;
}

.delete-btn:hover {
  background: #c82333;
}

/* Responsive design for full screen modals */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0;
  }
  
  .modal-content {
    border-radius: 0;
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-header h3 {
    font-size: 1.3rem;
  }
  
  .close-btn {
    width: 36px;
    height: 36px;
    font-size: 1.5rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .modal-footer {
    padding: 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .modal-footer button {
    width: 100%;
  }
  
  .vocabulary-pair {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    text-align: center;
  }
  
  .arrow {
    align-self: center;
    transform: rotate(90deg);
  }
  
  .lyrics-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .btn-copy-lyrics,
  .btn-play-with-lyrics,
  .btn-generate-lyrics {
    width: 100%;
    max-width: 300px;
  }
  
  .topic-name {
    font-size: 1.3rem;
    padding: 1rem;
  }
  
  .english-term,
  .spanish-term {
    font-size: 1rem;
  }
  
  .lyrics-text {
    padding: 1rem;
    font-size: 1rem;
    line-height: 1.6;
  }
}

/* Custom scrollbar for modal content */
.modal-body::-webkit-scrollbar {
  width:  8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.btn-analyze {
  background: #17a2b8;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-analyze:hover:not(:disabled) {
  background: #138496;
}

.btn-analyze:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.btn-generate-lyrics {
  background: #28a745;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-generate-lyrics:hover:not(:disabled) {
  background: #218838;
}

.btn-generate-lyrics:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.btn-generate-music {
  background: #6f42c1;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-generate-music:hover:not(:disabled) {
  background: #5a2d91;
}

.btn-generate-music:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.task-details-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.task-details-btn:hover:not(:disabled) {
  background: #0056b3;
}

.task-details-btn:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.btn-delete-photo {
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-delete-photo:hover {
  background: #c82333;
}

.btn-primary {
  background: #43a047;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #388e3c;
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-danger {
  background: #f44336;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-danger:hover {
  background: #d32f2f;
}

/* Add missing camera styles */
.camera-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.camera-modal {
  background: black;
  border-radius: 12px;
  padding: 1rem;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.camera-preview-container {
  position: relative;
  width: 100%;
  max-width: 400px;
}

.camera-video {
  width: 100%;
  aspect-ratio: 0.773;
  border-radius: 8px;
  object-fit: cover;
}

.document-frame {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-corners {
  position: absolute;
  inset: 10%;
  border: 2px solid rgba(67, 160, 71, 0.8);
  border-radius: 4px;
}

.corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 3px solid #43a047;
}

.corner.top-left {
  top: -3px;
  left: -3px;
  border-right: none;
  border-bottom: none;
}

.corner.top-right {
  top: -3px;
  right: -3px;
  border-left: none;
  border-bottom: none;
}

.corner.bottom-left {
  bottom: -3px;
  left: -3px;
  border-right: none;
  border-top: none;
}

.corner.bottom-right {
  bottom: -3px;
  right: -3px;
  border-left: none;
  border-top: none;
}

.document-guide-text {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 0.9rem;
  text-align: center;
  background: rgba(0, 0, 0, 0.7);
  padding: 0.5rem 1rem;
  border-radius: 4px;
}

.camera-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.action-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.capture-btn {
  background: #43a047;
  color: white;
}

.capture-btn:hover {
  background: #388e3c;
}

.cancel-btn {
  background: #6c757d;
  color: white;
}

.cancel-btn:hover {
  background: #5a6268;
}

.play-btn {
  background: #43a047;
  color: white;
}

.play-btn:hover {
  background: #388e3c;
}

/* Add missing form styles */
.earworm-form {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.form-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #eee;
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section h3 {
  margin: 0 0 1.5rem 0;
  color: #333;
  font-size: 1.3rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #43a047;
}

.image-upload-buttons {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0;
}

.upload-buttons-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
}

.upload-buttons-row .upload-btn {
  width: 100%;
}

@media (max-width: 768px) {
  .upload-buttons-row {
    flex: 1 1 100%;
  }
}

.image-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.image-container {
  position: relative;
  display: inline-block;
  margin-bottom: 0.5rem;
}

.preview-image {
  max-width: 200px;
  max-height: 150px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
  transition: all 0.2s ease;
}

.preview-image.clickable {
  cursor: pointer;
  border: 2px solid transparent;
}

.preview-image.clickable:hover {
  border-color: #43a047;
  box-shadow: 0 2px 6px rgba(67, 160, 71, 0.3);
  transform: scale(1.1);
}

.delete-photo-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.delete-photo-btn:hover {
  background: rgba(220, 53, 69, 1);
}

.small-image-preview {
  display: inline-block;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.small-preview-image {
  max-width: 120px;
  max-height: 90px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.small-preview-image:hover {
  border-color: #43a047;
  box-shadow: 0 2px 6px rgba(67, 160, 71, 0.3);
  transform: scale(1.05);
}

.earworm-form-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}

.earworm-form-actions-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

</style>